import { useEffect, useState } from 'react';
import { BaseAlertBanner, BaseButton } from '@components';
import ClientLogo from '@components/logo';
import { BaseTitle } from '@components/typography/styled.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import FooterLogo from '@layouts/auth/footer-logo';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import ResetPasswordModal from '@routes/auth/___shared___/reset-password-modal';
import { Countdown } from '@routes/auth/account-locked/ui/countdown';
import { deleteLockUserTime, readLockUserTime } from '@store/services/localStorage.service.ts';
import dayjs from 'dayjs';

import { AlertSection, FooterLogoSection, FormSection, SuccessMessageSection } from '../___shared___/styled.tsx';
import { AuthSharedWrapper } from '../___shared___/styled.tsx';
import { LockedTimeSection } from './styled.ts';

const AccountLocked = () => {
    const { t } = useLingui();
    const navigate = useCustomNavigate();
    const email = useAppSelector((state) => state.authLogin.email);

    const [isResetModalOpen, setIsResetModalOpen] = useState(false);

    const lockTimeEndUtc = readLockUserTime();
    const [minutesLeft, setMinutesLeft] = useState<number>(0);

    useEffect(() => {
        if (!lockTimeEndUtc) {
            setMinutesLeft(0);
            return;
        }

        const mins = Math.ceil(dayjs(lockTimeEndUtc).diff(dayjs(), 'minute', true));
        setMinutesLeft(mins);
    }, [lockTimeEndUtc]);

    const MINUTE_LABEL = minutesLeft === 1 ? t`minute` : t`minutes`;
    const ACCOUNT_LOCKED_BANNER_DESCRIPTION = t`Too many failed login attempts. Try again in about ${minutesLeft} ${MINUTE_LABEL} or reset your password to unlock now.`;

    const onUnlock = () => {
        deleteLockUserTime();
        navigate(ROUTES.AUTH.LOGIN, { replace: true });
    };

    return (
        <AuthSharedWrapper>
            <FormSection $maxWidth={580}>
                <ClientLogo />

                <SuccessMessageSection>
                    <BaseTitle>{t`Account locked`}</BaseTitle>
                </SuccessMessageSection>

                <AlertSection>
                    <BaseAlertBanner
                        type="error"
                        description={
                            <>
                                {ACCOUNT_LOCKED_BANNER_DESCRIPTION}
                                <br />
                                <br />
                                <LockedTimeSection>
                                    {t`Wait`} (
                                    <Countdown
                                        lockTimeEndUtc={lockTimeEndUtc}
                                        onExpire={() => {
                                            onUnlock();
                                        }}
                                    />
                                    )
                                </LockedTimeSection>
                            </>
                        }
                    />
                </AlertSection>

                <BaseButton $customType="primary" onClick={() => setIsResetModalOpen(true)}>
                    {t`Reset password`}
                </BaseButton>
            </FormSection>

            <FooterLogoSection>
                <FooterLogo />
            </FooterLogoSection>

            <ResetPasswordModal open={isResetModalOpen} email={email} onClose={() => setIsResetModalOpen(false)} />
        </AuthSharedWrapper>
    );
};

export default AccountLocked;
